/**
 * SCWT-PM-V2 Phase 6 · PM Corrective-Action Leads tab body.
 *
 * Shows the agency's warm leads sent to PWS Partners. Manual-only
 * creation per Phase 6 approval — no auto-trigger. Each row surfaces
 * the current status, offer-expiry countdown (for `offered`), and
 * a Withdraw action for anything not already terminal.
 *
 * Feature-gated on `pm_corrective_lead_pipeline` by the parent tab
 * strip; this component assumes it's mounted only when the flag is
 * visible for the caller.
 */
import { useEffect, useState } from "react";
import { RefreshCw, Plus, XCircle, ExternalLink, CheckCircle2, Clock, Building2 } from "lucide-react";
import api from "@/lib/api";
import { CL_STATUS_LABEL, CL_STATUS_TONE, offerCountdown, fmtLocal } from "@/lib/correctiveLeads";
import PmCreateCorrectiveLeadDrawer from "./PmCreateCorrectiveLeadDrawer";

const TERMINAL = new Set([
  "declined", "offer_expired", "consent_withdrawn", "work_completed",
  "not_proceeding", "withdrawn_by_pm", "withdrawn_by_landlord",
]);

export default function PmCorrectiveLeadsTab() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busyId, setBusyId]     = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/property-manager/corrective-leads");
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load corrective leads.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const withdraw = async (row) => {
    const reason = prompt(
      "Withdraw this lead?\n\nOptional reason (visible to the Partner + audit log):",
      "",
    );
    if (reason === null) return; // cancelled
    setBusyId(row.id);
    try {
      await api.post(`/property-manager/corrective-leads/${row.id}/withdraw`,
        { reason: reason || "" });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not withdraw the lead.");
    } finally { setBusyId(null); }
  };

  return (
    <section data-testid="pm-tab-body-corrective-leads" className="mt-2">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
            Corrective-action leads
          </h2>
          <p className="mt-1.5 text-sm text-slate-600 max-w-2xl leading-relaxed">
            Send a warm lead to your assigned PWS Partner from a published
            report. Landlord contact stays hidden until the Partner accepts
            AND landlord consent is active. Offers expire after 72 hours.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            data-testid="pm-corrective-leads-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-full px-3 py-2"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            type="button"
            data-testid="pm-corrective-leads-create-btn"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold px-4 py-2 shadow-sm"
          >
            <Plus className="w-4 h-4" /> Send corrective lead
          </button>
        </div>
      </div>

      {loading && (
        <div data-testid="pm-corrective-leads-loading" className="text-sm text-slate-500 py-10 text-center">
          Loading corrective leads…
        </div>
      )}

      {!loading && error && (
        <div data-testid="pm-corrective-leads-error"
             className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div data-testid="pm-corrective-leads-empty"
             className="rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center">
          <Building2 className="w-9 h-9 text-cyan-500 mx-auto mb-3" />
          <div className="font-display font-semibold text-slate-900">
            No corrective leads sent yet
          </div>
          <p className="text-slate-500 text-sm mt-1.5 max-w-md mx-auto">
            When you send a lead to a PWS Partner from a published report,
            you&apos;ll see the status here in real-time.
          </p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <ul data-testid="pm-corrective-leads-list" className="grid gap-3">
          {items.map((row) => (
            <PmLeadRow
              key={row.id}
              row={row}
              busy={busyId === row.id}
              onWithdraw={() => withdraw(row)}
            />
          ))}
        </ul>
      )}

      <PmCreateCorrectiveLeadDrawer
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); load(); }}
      />
    </section>
  );
}

function PmLeadRow({ row, busy, onWithdraw }) {
  const partner = row.service_partner_snapshot || {};
  const tone = CL_STATUS_TONE[row.status] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = CL_STATUS_LABEL[row.status] || row.status;
  const isOffered = row.status === "offered";
  const canWithdraw = !TERMINAL.has(row.status)
    && !["withdrawn_by_pm", "withdrawn_by_landlord"].includes(row.status);
  const codes = row.corrective_action_codes || [];
  return (
    <li
      data-testid={`pm-corrective-lead-${row.id}`}
      className="bg-white rounded-2xl border border-slate-200 hover:border-cyan-200 hover:shadow transition-all p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-xs sm:text-sm text-slate-500 flex-shrink-0">
            {row.reference}
          </span>
          <span
            data-testid={`pm-corrective-lead-status-${row.id}`}
            className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full border px-2.5 py-1 ${tone}`}
          >
            {row.status === "accepted" && <CheckCircle2 className="w-3 h-3" />}
            {label}
          </span>
          {isOffered && (
            <span
              data-testid={`pm-corrective-lead-countdown-${row.id}`}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700"
            >
              <Clock className="w-3 h-3" />
              {offerCountdown(row.offer_expires_at)}
            </span>
          )}
        </div>
        {canWithdraw && (
          <button
            type="button"
            data-testid={`pm-corrective-lead-withdraw-${row.id}`}
            disabled={busy}
            onClick={onWithdraw}
            className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 hover:text-rose-900 disabled:opacity-40"
          >
            <XCircle className="w-3.5 h-3.5" /> Withdraw
          </button>
        )}
      </div>

      <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">PWS Partner</div>
          <div className="mt-0.5 text-slate-900 font-semibold">
            {partner.name || "—"}
            {partner.region && (
              <span className="ml-2 text-xs text-slate-500 font-normal">({partner.region})</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Offered at</div>
          <div className="mt-0.5 text-slate-700">{fmtLocal(row.offered_at)}</div>
        </div>
        {codes.length > 0 && (
          <div className="sm:col-span-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Actions</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {codes.map((c) => (
                <span key={c} className="text-[11px] rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">{c}</span>
              ))}
            </div>
          </div>
        )}
        {row.landlord_contact_snapshot && (
          <div
            data-testid={`pm-corrective-lead-contact-revealed-${row.id}`}
            className="sm:col-span-2 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 inline-flex items-center gap-2"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Contact shared with Partner on {fmtLocal(row.contact_revealed_at)}.
          </div>
        )}
        {row.status === "consent_withdrawn" && (
          <div className="sm:col-span-2 rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800">
            Landlord withdrew their consent — Partner can no longer act on this lead.
          </div>
        )}
        {row.decline_reason && (
          <div className="sm:col-span-2 rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
            <strong className="font-semibold">Partner declined:</strong> {row.decline_reason}
          </div>
        )}
        {row.optional_pm_message && (
          <div className="sm:col-span-2 text-xs text-slate-600">
            <span className="font-semibold text-slate-800">Your message:</span> {row.optional_pm_message}
          </div>
        )}
      </div>
    </li>
  );
}
